// Sustemia · Balance Hídrico · motor de cálculo
// Portado de Sustemia_BalanceHidrico_Modulo_ES_v1_2026-08-11.html
// (gradoDe, semaforo, evalBench, armar) y generalizado contra el estado real
// de la app (en vez de los objetos ad-hoc E/o del prototipo).
//
// Reglas de diseño no negociables (ver Fuentes.md §3 — "lo que el módulo se
// niega a hacer"), todas aplicadas acá:
//   - No estima el efluente como "la entrada menos un porcentaje": el
//     descuadre se calcula, nunca al revés.
//   - No emite veredicto mientras la carga está a medias (`cerrado===false`
//     o `pendientes>0` ⇒ semáforo informativo, no diagnóstico).
//   - No obliga a repartir el 100% de la diferencia: `clasificacionResiduo`
//     puede quedar parcial y el motor lo declara, no lo fuerza.
//   - No compara donde no hay benchmark publicado (`evalBenchmark`).
//   - No cuenta dos veces un medidor aguas abajo de otro
//     (`medidorAguasAbajoValido`).
//
// El margen de cierre (10%, ver `semaforo()`) es CRITERIO INTERNO DE
// SUSTEMIA, sin fuente publicada (ver Fuentes.md §2), confirmado por el
// cliente — no un umbral normativo.

import { ORIGENES } from "./catalogs/instrumentos";

// ---------------------------------------------------------------------------
// GRADO DE VALIDEZ (escala 1-5, adaptada de IWA/AWWA M36)
// ---------------------------------------------------------------------------

/**
 * Deriva el grado de validez de un flujo a partir de su origen declarado.
 * Sin evidencia adjunta el grado nunca pasa de 3, se diga lo que se diga
 * (ver Guía de campo §1.1: "el módulo nunca pide un número, deriva el grado
 * de la evidencia").
 */
export function gradoDe(origen, calibracionVigente, evidenciaAdjunta) {
  const base = ORIGENES[origen] ? ORIGENES[origen].grado : 1;
  let grado = base;
  if (origen === "medidor" && !calibracionVigente) grado = 4;
  if (!evidenciaAdjunta) grado = Math.min(grado, 3);
  return grado;
}

/**
 * Un instrumento inadecuado para el fluido no pasa nunca de grado 3, aunque
 * la calibración esté vigente (Fuentes.md §3).
 */
export function gradoConAptitudInstrumento(gradoBase, instrumentoApto) {
  return instrumentoApto === false ? Math.min(gradoBase, 3) : gradoBase;
}

/**
 * Mismo criterio que gradoDe(): sin evidencia adjunta, el grado nunca pasa
 * de 3 — tampoco en las fichas de campo ejecutadas. "El grado 5 exige
 * evidencia adjunta" (Fuentes.md §5: pendiente resuelto acá — la evidencia
 * deja de ser un tilde y pasa a ser un archivo real).
 */
export function gradoConEvidencia(gradoBase, evidenciaAdjunta) {
  return evidenciaAdjunta ? gradoBase : Math.min(gradoBase, 3);
}

// ---------------------------------------------------------------------------
// ÍNDICE DE CALIDAD DEL DATO
// ---------------------------------------------------------------------------

/** Promedio ponderado por volumen de los grados de todos los flujos. */
export function calcularIndiceCalidad(flujos) {
  const vol = flujos.reduce((a, f) => a + Math.abs(f.volumenM3 || 0), 0);
  if (!vol) return 0;
  const suma = flujos.reduce((a, f) => a + Math.abs(f.volumenM3 || 0) * (f.grado || 0), 0);
  return +(suma / vol).toFixed(2);
}

// Meta interna de Sustemia para el índice de calidad: 4,00 sobre 5. Es un
// umbral propio, no sale de ninguna norma (motor de cálculo — "Cómo se
// deriva la calidad del dato"), igual que el margen de cierre del 10%.
export const META_INDICE_CALIDAD = 4.0;

// Validación V10: más del 30% del volumen atribuido como "estimado" es una
// recomendación (priorizar el plan de medición), nunca una no conformidad —
// umbral interno de Sustemia, sin fuente publicada.
export const UMBRAL_ESTIMACIONES_PCT = 30;

// ---------------------------------------------------------------------------
// DESCUADRE — Entradas = Salidas + Δ inventario
// ---------------------------------------------------------------------------

export function calcularDescuadre({ totalEntradas, totalSalidas, deltaInventarioM3 = 0 }) {
  const descuadre = totalEntradas - totalSalidas - deltaInventarioM3;
  const descuadrePct = totalEntradas ? (descuadre / totalEntradas) * 100 : 0;
  return { descuadre, descuadrePct };
}

// ---------------------------------------------------------------------------
// SEMÁFORO — no emite veredicto mientras la carga está a medias
// ---------------------------------------------------------------------------

export function semaforo({ descuadre, descuadrePct, pendientes = 0, cerrado, vacio }) {
  if (vacio) {
    return { clave: "vacio", severidad: "info", titulo: "Todavía no cargaste nada", detalle: "Empezá por el agua que entra: sin eso, no hay contra qué comparar lo que sale." };
  }
  if (!cerrado) {
    return { clave: "cargando", severidad: "info", titulo: "Esto es un inventario, no un resultado", detalle: "Mientras estés cargando, la diferencia va a ser grande y no significa nada." };
  }
  if (pendientes > 0) {
    return { clave: "parcial", severidad: "info", titulo: "Todavía no está cerrado", detalle: `Falta${pendientes > 1 ? "n" : ""} ${pendientes} dato${pendientes > 1 ? "s" : ""}.` };
  }
  if (descuadre < 0) {
    return { clave: "negativo", severidad: "stop", titulo: "Sale más agua de la que entra", detalle: "Hay una entrada sin declarar o una salida sobreestimada. Así no se puede cerrar el período." };
  }
  if (descuadre === 0) {
    return { clave: "cero", severidad: "stop", titulo: "Cierra exacto en cero", detalle: "Un balance real no cierra en cero. Casi siempre significa que una salida se calculó como el resto de la resta." };
  }
  const p = descuadrePct;
  if (p <= 5) return { clave: "a", severidad: "ok", titulo: "Cierra muy bien", detalle: "Queda sin explicar menos del 5 %." };
  // Margen de cierre confirmado por el cliente en 10% (ver consulta A·01):
  // reemplaza la banda propuesta de 10-15%, que quedaba marcada como
  // "criterio interno de Sustemia, sin fuente publicada". Ya no hay una
  // franja "con salvedad" entre el cierre bueno y el que hay que explicar.
  if (p <= 10) return { clave: "b", severidad: "ok", titulo: "Cierra bien", detalle: "Queda sin explicar menos del 10 %." };
  if (p <= 25) return { clave: "d", severidad: "alerta", titulo: "Hay que explicar la diferencia", detalle: "Más del 10 % sin explicar. Antes de presentarlo hay que descomponerlo." };
  return { clave: "e", severidad: "stop", titulo: "La diferencia es demasiado grande", detalle: "Más del 25 % sin explicar. El balance no se sostiene sin una justificación técnica cargada." };
}

// ---------------------------------------------------------------------------
// BENCHMARK — no compara donde no hay rango publicado
// ---------------------------------------------------------------------------

export function evalBenchmark(procesoConBenchmark, valorEspecifico) {
  if (!procesoConBenchmark || !procesoConBenchmark.bench) {
    return { disponible: false, motivo: (procesoConBenchmark && procesoConBenchmark.motivo) || "Sin rango publicado verificado." };
  }
  if (valorEspecifico == null) {
    return { disponible: false, motivo: "Falta la producción del período." };
  }
  const { min, max, unidad } = procesoConBenchmark.bench;
  const dentroDelRango = valorEspecifico >= min && valorEspecifico <= max;
  const desviacionPct = valorEspecifico > max ? ((valorEspecifico - max) / max) * 100 : valorEspecifico < min ? ((valorEspecifico - min) / min) * 100 : 0;
  return { disponible: true, valorEspecifico, min, max, unidad, dentroDelRango, desviacionPct };
}

// ---------------------------------------------------------------------------
// CLASIFICACIÓN DEL RESIDUO (M36) — no obliga a repartir el 100%
// ---------------------------------------------------------------------------

/** El período no cierra si queda residuo sin clasificar (Fuentes.md §3/§4). */
export function residuoSinClasificar(descuadre, clasificacionResiduo = []) {
  if (descuadre <= 0) return false;
  const clasificado = clasificacionResiduo.reduce((a, c) => a + (c.volumenM3 || 0), 0);
  return clasificado + 1e-6 < descuadre;
}

/**
 * Complemento de `residuoSinClasificar`: Σ clasificaciones ≤ sin_explicar es
 * un límite matemático (motor de cálculo §16), no solo un mínimo. Clasificar
 * más volumen del que hay que explicar es tan inválido como dejarlo vacío.
 */
export function residuoSobreClasificado(descuadre, clasificacionResiduo = []) {
  const clasificado = clasificacionResiduo.reduce((a, c) => a + (c.volumenM3 || 0), 0);
  return clasificado > Math.max(descuadre, 0) + 1e-6;
}

// ---------------------------------------------------------------------------
// CONTROLES AUTOMÁTICOS (Fuentes.md §4 — "controles que corren solos")
// ---------------------------------------------------------------------------

export function lecturaValida(inicial, final) {
  return final >= inicial;
}

/**
 * Motor de cálculo §1: un consumo negativo (lectura_final − lectura_inicial
 * < 0, o cualquier flujo cargado en negativo por error) bloquea el período
 * — no es solo un aviso en la ficha que lo originó.
 */
export function hayLecturaInvalida(entradas = [], salidas = []) {
  return [...entradas, ...salidas].some((f) => (f.volumenM3 || 0) < 0);
}

export function sumaSubmedidoresValida(sumaSubmedidoresM3, entradaTotalM3) {
  return sumaSubmedidoresM3 <= entradaTotalM3 + 1e-6;
}

/** Un medidor con padre declarado y rol distinto de interno es doble conteo. */
export function medidorAguasAbajoValido(medidor) {
  if (!medidor.medidorPadreId) return true;
  return medidor.rolMedidor === "interno";
}

export function repartoSuperaTecho(sumaRepartosM3, lecturaMedidorM3) {
  return sumaRepartosM3 > lecturaMedidorM3 + 1e-6;
}

// ---------------------------------------------------------------------------
// PARETO DE USOS SIGNIFICATIVOS (informe §16)
// ---------------------------------------------------------------------------

/**
 * @param {number} sinAtribuirM3 - residuo de `coberturaDeSalidas` (motor de
 *   cálculo §14): compite en el ranking como un uso más (§15), no se omite.
 */
export function paretoDeUsos(salidas, sinAtribuirM3 = 0) {
  const conResiduo =
    sinAtribuirM3 > 0
      ? [...salidas, { id: "sin_atribuir", nombre: "Sin atribuir a ningún proceso", volumenM3: sinAtribuirM3 }]
      : salidas;
  const ordenado = [...conResiduo].filter((s) => (s.volumenM3 || 0) > 0).sort((a, b) => b.volumenM3 - a.volumenM3);
  const total = ordenado.reduce((a, s) => a + s.volumenM3, 0);
  let acumulado = 0;
  return ordenado.map((s) => {
    acumulado += s.volumenM3;
    const pctAcumulado = total ? (acumulado / total) * 100 : 0;
    return { ...s, pct: total ? (s.volumenM3 / total) * 100 : 0, pctAcumulado, dentroDel80: pctAcumulado <= 80 };
  });
}

// ---------------------------------------------------------------------------
// PLAN DE MEDICIÓN — ordenado por impacto sobre el índice de calidad (§20)
// ---------------------------------------------------------------------------

export function planDeMedicion(flujos) {
  const volTotal = flujos.reduce((a, f) => a + Math.abs(f.volumenM3 || 0), 0);
  if (!volTotal) return [];
  return flujos
    .filter((f) => (f.grado || 0) < 5 && (f.volumenM3 || 0) > 0)
    .map((f) => ({
      ...f,
      gananciaIndiceEstimada: +((Math.abs(f.volumenM3) * (5 - f.grado)) / volTotal).toFixed(3),
    }))
    .sort((a, b) => b.gananciaIndiceEstimada - a.gananciaIndiceEstimada);
}

// ---------------------------------------------------------------------------
// MEMORIAS DE CÁLCULO POR CASO DE CAMPO (catalogs/casosDeCampo.js)
// El motor sigue siendo la única fuente de verdad: las fichas describen el
// método, esto lo ejecuta.
// ---------------------------------------------------------------------------

/** Caso A2 — evaporación de torre por diferencia. */
export function calcularCasoA2({ reposicionMedida, purgaMedida }) {
  const evaporacionM3 = reposicionMedida - purgaMedida;
  const ciclosDeConcentracion = purgaMedida ? +(reposicionMedida / purgaMedida).toFixed(2) : null;
  return { evaporacionM3, ciclosDeConcentracion };
}

/**
 * Caso A3 — vapor perdido en caldera, por diferencia entre reposición y
 * purga. Mismo principio que la torre (A2): la purga vuelve al efluente y
 * ya se mide ahí, así que solo el vapor que no vuelve cuenta como salida.
 * A propósito NO devuelve "ciclos de concentración": es un concepto de
 * torres de enfriamiento, no de calderas.
 */
export function calcularCasoCaldera({ reposicionMedida, purgaMedida }) {
  const vaporM3 = reposicionMedida - purgaMedida;
  return { vaporM3 };
}

/** Caso B1 — medidor compartido. El medidor es el techo: no se reparte el 100%. */
export function calcularCasoB1({ cargas, kgPorCarga, relacionDeBanoLKg, personas, diasOperativos, litrosPorPersonaDia, medidorM3 }) {
  const tinturaM3 = (cargas * kgPorCarga * relacionDeBanoLKg) / 1000;
  const domesticoM3 = (personas * diasOperativos * litrosPorPersonaDia) / 1000;
  const noAsignadoM3 = medidorM3 - tinturaM3 - domesticoM3;
  return { tinturaM3, domesticoM3, noAsignadoM3 };
}

/** Caso B2 — variación de inventario. El signo entra en la ecuación. */
export function calcularCasoB2({ diametro, nivelInicial, nivelFinal }) {
  const area = Math.PI * (diametro / 2) ** 2;
  const volumenInicialM3 = area * nivelInicial;
  const volumenFinalM3 = area * nivelFinal;
  const deltaInventarioM3 = volumenFinalM3 - volumenInicialM3;
  return { area: +area.toFixed(4), volumenInicialM3, volumenFinalM3, deltaInventarioM3 };
}

/** Caso C1 — lavandería por relación de baño (todos los baños del ciclo). */
export function calcularCasoC1({ prendas, pesoMedioPorPrendaKg, relacionDeBanoAcumuladaLKg }) {
  const kgProcesados = prendas * pesoMedioPorPrendaKg;
  const volumenM3 = (kgProcesados * relacionDeBanoAcumuladaLKg) / 1000;
  return { kgProcesados, volumenM3 };
}

// ---------------------------------------------------------------------------
// COSTEO MFCA (ISO 14051) — costo de flujos de materiales por centro de
// cantidad. ISO 14051 exige que el costeo incluya, además del agua, la
// energía térmica, los químicos y el tratamiento asociados a cada flujo
// (Fuentes.md §1). Es lo que hace que dirección apruebe el plan de medición
// (Fuentes.md §5, punto 4: "Falta el costeo").
// ---------------------------------------------------------------------------

/** Costo de un volumen dado, desglosado por categoría (nunca solo un total). */
export function costoDeFlujo(volumenM3, tarifas = {}) {
  const aguaPorM3 = Number(tarifas.aguaPorM3) || 0;
  const energiaTermicaPorM3 = Number(tarifas.energiaTermicaPorM3) || 0;
  const quimicosPorM3 = Number(tarifas.quimicosPorM3) || 0;
  const tratamientoPorM3 = Number(tarifas.tratamientoPorM3) || 0;
  const vol = Number(volumenM3) || 0;

  const agua = vol * aguaPorM3;
  const energiaTermica = vol * energiaTermicaPorM3;
  const quimicos = vol * quimicosPorM3;
  const tratamiento = vol * tratamientoPorM3;
  return { agua, energiaTermica, quimicos, tratamiento, total: agua + energiaTermica + quimicos + tratamiento };
}

/**
 * Costea cada centro de cantidad sumando sus salidas. Un centro con tarifa
 * propia declarada (`tarifasPorCentro`) la usa; si no, cae a la tarifa por
 * defecto del sitio — mismo criterio de "cascada" que el resto del módulo
 * usa para no repetir un dato que ya está declarado en otro lado.
 */
export function costearCentros(salidas, tarifasPorDefecto, tarifasPorCentro = []) {
  const volumenPorCentro = {};
  for (const s of salidas || []) {
    const key = s.centroId || "sin_centro";
    volumenPorCentro[key] = (volumenPorCentro[key] || 0) + (Number(s.volumenM3) || 0);
  }
  return Object.entries(volumenPorCentro).map(([centroId, volumenM3]) => {
    const override = (tarifasPorCentro || []).find((t) => t.centroId === centroId);
    return { centroId, volumenM3, ...costoDeFlujo(volumenM3, override || tarifasPorDefecto) };
  });
}

/**
 * El costo de la pérdida real (IWA/AWWA M36), no de todo el descuadre — es
 * el número que se usa para justificar el plan de medición ante dirección.
 */
export function costoDeLaPerdida(clasificacionResiduo, tarifasPorDefecto) {
  const volumenM3 = (clasificacionResiduo || [])
    .filter((c) => c.categoria === "real")
    .reduce((a, c) => a + (Number(c.volumenM3) || 0), 0);
  return { volumenM3, ...costoDeFlujo(volumenM3, tarifasPorDefecto) };
}

// ---------------------------------------------------------------------------
// COBERTURA DE MEDICIÓN (motor de cálculo §14 / informe §16)
// medido / estimado, sobre las ENTRADAS — no sobre la suma de las salidas.
// sin_atribuir = entradas − (medido + estimado): es un RESIDUO, no una
// etiqueta que el usuario declare por salida (así lo hace el prototipo
// original: sinAtribuir=Math.max(0,totalIn-atribuido)). El reparto por proxy
// queda aparte, sin entrar en `atribuido` (Fuentes.md §3: no suma al
// balance, ver `soloInformativo` en estado.js#buildEstadoParaMotor).
// ---------------------------------------------------------------------------

const CATEGORIA_DE_ORIGEN = {
  medidor: "medido",
  compartido: "medido",
  factura: "medido",
  estim_mem: "estimado",
  estim_sin: "estimado",
  reparto: "reparto",
};

export function coberturaDeSalidas(salidas, totalEntradasM3 = 0) {
  const categorias = { medido: 0, estimado: 0, reparto: 0 };
  for (const s of salidas || []) {
    const cat = CATEGORIA_DE_ORIGEN[s.origen];
    if (cat) categorias[cat] += Number(s.volumenM3) || 0;
  }
  const atribuido = categorias.medido + categorias.estimado;
  const sinAtribuir = Math.max(0, totalEntradasM3 - atribuido);
  const filas = [
    { categoria: "medido", volumenM3: categorias.medido },
    { categoria: "estimado", volumenM3: categorias.estimado },
    { categoria: "sin_atribuir", volumenM3: sinAtribuir },
    { categoria: "reparto", volumenM3: categorias.reparto },
  ];
  return filas.map((f) => ({ ...f, pct: totalEntradasM3 ? +((f.volumenM3 / totalEntradasM3) * 100).toFixed(1) : 0 }));
}

// ---------------------------------------------------------------------------
// ESTIMADORES SIN INSTRUMENTOS (modo "No tengo medidores")
// Portados de SIN_NADA en Sustemia_BalanceHidrico_Modulo_ES_v1.html. Ninguno
// pasa de grado 3 — son formas de arrancar sin comprar nada, no lecturas.
// ---------------------------------------------------------------------------

/** Aforo por llenado de un volumen conocido (ej. tanque) y tiempo. */
export function aforoPorLlenado({ volumenTanqueM3, minutosEnLlenar, horasPorDia, diasPorAnio }) {
  const caudalM3h = minutosEnLlenar ? volumenTanqueM3 / (minutosEnLlenar / 60) : 0;
  return { caudalM3h, volumenAnualM3: caudalM3h * horasPorDia * diasPorAnio };
}

/** Aforo por caudal directo (ej. balde y cronómetro): L/s → m³/h. */
export function aforoPorCaudal({ litros, segundos, horasPorDia, diasPorAnio }) {
  const caudalM3h = segundos ? ((litros / segundos) * 3.6) : 0;
  return { caudalM3h, volumenAnualM3: caudalM3h * horasPorDia * diasPorAnio };
}

/** Agua comprada en camión: viajes × m³ declarados por remito. */
export function estimacionPorViajes({ viajes, m3PorViaje }) {
  return (Number(viajes) || 0) * (Number(m3PorViaje) || 0);
}

/** Consumo doméstico: personas × días × litros/persona-día (HOWARD & BARTRAM). */
export function estimacionDomesticaAnual({ personas, dias, litrosPorPersonaDia }) {
  return ((Number(personas) || 0) * (Number(dias) || 0) * (Number(litrosPorPersonaDia) || 0)) / 1000;
}

/** Proceso húmedo por ficha: kg de producción × relación de baño (L/kg). */
export function estimacionPorProduccion({ kg, relacionDeBanoLKg }) {
  return ((Number(kg) || 0) * (Number(relacionDeBanoLKg) || 0)) / 1000;
}

// ---------------------------------------------------------------------------
// MOTOR — orquesta todo lo anterior sobre el estado real del Balance
// ---------------------------------------------------------------------------

/**
 * @param {object} estado
 * @param {Array}  estado.entradas  - [{ id, nombre, volumenM3, grado }]
 * @param {Array}  estado.salidas   - [{ id, nombre, volumenM3, grado, soloInformativo }]
 * @param {number} estado.deltaInventarioM3
 * @param {boolean} estado.cerrado  - el usuario dio la carga por terminada
 * @param {Array}  estado.clasificacionResiduo - [{ categoria, volumenM3 }]
 * @param {number} estado.divergenciasSinResolver
 * @param {boolean} [estado.dobleConteoInstrumentos] - algún instrumento con
 *   padre declarado y rol distinto de interno (V16, bloqueante)
 * @param {object|null} estado.benchmark - { proceso: {bench,...}, valorEspecifico }
 */
export function recalcularBalance(estado) {
  const entradas = estado.entradas || [];
  const salidasTodas = estado.salidas || [];
  const salidas = salidasTodas.filter((s) => !s.soloInformativo);
  const pendientes = salidasTodas.filter((s) => s.pendiente && !s.soloInformativo).length;

  const totalEntradas = entradas.reduce((a, f) => a + (f.volumenM3 || 0), 0);
  const totalSalidas = salidas.reduce((a, f) => a + (f.volumenM3 || 0), 0);
  const deltaInventarioM3 = estado.deltaInventarioM3 || 0;

  const { descuadre, descuadrePct } = calcularDescuadre({ totalEntradas, totalSalidas, deltaInventarioM3 });

  const vacio = entradas.length === 0 && salidas.length === 0;
  const sem = semaforo({ descuadre, descuadrePct, pendientes, cerrado: !!estado.cerrado, vacio });

  const todosLosFlujos = [...entradas, ...salidas.filter((s) => (s.volumenM3 || 0) > 0)];
  const indiceCalidad = calcularIndiceCalidad(todosLosFlujos);

  const cobertura = coberturaDeSalidas(salidas, totalEntradas);
  const sinAtribuirM3 = (cobertura.find((c) => c.categoria === "sin_atribuir") || {}).volumenM3 || 0;
  const pareto = paretoDeUsos(salidas, sinAtribuirM3);
  const pctEstimado = (cobertura.find((c) => c.categoria === "estimado") || {}).pct || 0;

  const residuoBloquea = residuoSinClasificar(descuadre, estado.clasificacionResiduo);
  const residuoExcedido = residuoSobreClasificado(descuadre, estado.clasificacionResiduo);
  const divergenciasBloquean = (estado.divergenciasSinResolver || 0) > 0;
  const lecturaInvalida = hayLecturaInvalida(entradas, salidasTodas);
  // V03/V04 (validaciones): un descuadre negativo o exactamente cero es tan
  // inválido como un dato a medio cargar — el propio semáforo ya lo dice
  // ("no se puede cerrar", "un balance real no cierra en cero"), esto lo
  // hace valer en cierraOk, no solo en el color del mensaje.
  const descuadreInvalido = ["negativo", "cero"].includes(sem.clave);
  // V16: un medidor con padre declarado y rol distinto de interno cuenta dos
  // veces un volumen que ya pasó por el macromedidor.
  const dobleConteoInstrumentos = !!estado.dobleConteoInstrumentos;

  const cierraOk =
    !["vacio", "cargando", "parcial"].includes(sem.clave) &&
    !descuadreInvalido &&
    !residuoBloquea &&
    !residuoExcedido &&
    !divergenciasBloquean &&
    !lecturaInvalida &&
    !dobleConteoInstrumentos;

  const benchmarkEval = estado.benchmark
    ? evalBenchmark(estado.benchmark.proceso, estado.benchmark.valorEspecifico)
    : { disponible: false, motivo: "No aplica para esta tipología." };

  return {
    totalEntradas,
    totalSalidas,
    deltaInventarioM3,
    descuadre,
    descuadrePct,
    semaforo: sem,
    indiceCalidad,
    benchmark: benchmarkEval,
    cobertura,
    pareto,
    planDeMedicion: planDeMedicion(todosLosFlujos),
    bloqueos: {
      residuoSinClasificar: residuoBloquea,
      residuoSobreClasificado: residuoExcedido,
      divergenciasSinResolver: divergenciasBloquean,
      lecturaInvalida,
      descuadreInvalido,
      dobleConteoInstrumentos,
    },
    // Advertencias (validaciones V10/V11): avisan y dejan seguir, no
    // bloquean el cierre.
    advertencias: {
      estimacionesAltas: pctEstimado > UMBRAL_ESTIMACIONES_PCT,
      sinAtribuirEnTop80: pareto.some((p) => p.id === "sin_atribuir" && p.dentroDel80),
    },
    cierraOk,
  };
}

export default {
  gradoDe,
  gradoConAptitudInstrumento,
  gradoConEvidencia,
  calcularCasoA2,
  calcularCasoCaldera,
  calcularCasoB1,
  calcularCasoB2,
  calcularCasoC1,
  calcularIndiceCalidad,
  META_INDICE_CALIDAD,
  UMBRAL_ESTIMACIONES_PCT,
  calcularDescuadre,
  semaforo,
  evalBenchmark,
  residuoSinClasificar,
  residuoSobreClasificado,
  lecturaValida,
  hayLecturaInvalida,
  sumaSubmedidoresValida,
  medidorAguasAbajoValido,
  repartoSuperaTecho,
  paretoDeUsos,
  planDeMedicion,
  coberturaDeSalidas,
  aforoPorLlenado,
  aforoPorCaudal,
  estimacionPorViajes,
  estimacionDomesticaAnual,
  estimacionPorProduccion,
  costoDeFlujo,
  costearCentros,
  costoDeLaPerdida,
  recalcularBalance,
};
